import { useEffect, useMemo, useState } from 'react';
import { Check, Eye, Loader2, MoveRight, Send, Square, X } from 'lucide-react';

import type { TtqModelInvocationOptions } from '@kelilearning/psycheflow-contracts';

import { formatTtqModelAuditDisplay } from '../../utils/ttqModelBrand';
import { TtqAngleRelationIcon } from './TtqAngleRelationIcon';
import {
  TTQ_BOTTOM_PANEL_L1_WIDTH_PX,
  TTQ_BOTTOM_PANEL_SHELL_CLASS,
  TTQ_BOTTOM_PANEL_SHELL_STYLE,
} from './ttqBottomPanelShell';
import { TtqWorkbenchModelMenu } from './TtqWorkbenchModelMenu';
import {
  getTtqWorkbenchModelOption,
  readStoredTtqWorkbenchModelId,
  toTtqModelInvocation,
} from './ttqWorkbenchModels';

export type Ttq1anqAssessPreview = {
  canUnify: boolean;
  reasonMarkdown: string;
  promptPreview: string;
  directionPreview?: string;
  modelAudit?: { provider?: string; model?: string; mode?: string } | null;
};

type TtqBottomPanel1anqProps = {
  visible: boolean;
  memberParticleIds: string[];
  /** 成员单行题干摘要（particleId → label） */
  memberLabels?: Record<string, string>;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onAssess: (payload: {
    memberParticleIds: string[];
    description: string;
    model?: TtqModelInvocationOptions;
  }) => Promise<Ttq1anqAssessPreview>;
  onSubmit: (payload: {
    memberParticleIds: string[];
    description: string;
    directionPreview?: string;
    promptPreview?: string;
    model?: TtqModelInvocationOptions;
  }) => void;
};

const L1_DESCRIPTION = '多题综合：由大模型判断能否共用同一解法，并生成一颗「一解多题」综合知识颗粒（可后续追加 member 再判定）。';

/** 鸟瞰 · 生成型一解多题（多对一）：先 assess 提示词预览，再发送生成 */
export function TtqBottomPanel1anq({
  visible,
  memberParticleIds,
  memberLabels = {},
  submitting = false,
  error = null,
  onClose,
  onAssess,
  onSubmit,
}: TtqBottomPanel1anqProps) {
  const [description, setDescription] = useState('');
  const [assessPreview, setAssessPreview] = useState<Ttq1anqAssessPreview | null>(null);
  const [assessing, setAssessing] = useState(false);
  const [assessError, setAssessError] = useState<string | null>(null);
  const [modelId, setModelId] = useState(() => readStoredTtqWorkbenchModelId());
  const selectedModel = useMemo(
    () => toTtqModelInvocation(getTtqWorkbenchModelOption(modelId)),
    [modelId],
  );

  const labelOf = (id: string) => {
    const label = String(memberLabels[id] || '').trim();
    return label || id.slice(0, 8);
  };

  useEffect(() => {
    if (!visible) {
      setDescription('');
      setAssessPreview(null);
      setAssessError(null);
      return;
    }
    setModelId(readStoredTtqWorkbenchModelId());
  }, [visible]);

  useEffect(() => {
    setAssessPreview(null);
    setAssessError(null);
  }, [description, memberParticleIds]);

  if (!visible || memberParticleIds.length < 2) return null;

  const busy = submitting || assessing;
  const hasPreview = Boolean(assessPreview?.promptPreview?.trim());
  const forceUnify = assessPreview?.canUnify === false;
  const canSubmit = !busy && hasPreview;
  const previewModel = formatTtqModelAuditDisplay(assessPreview?.modelAudit);

  const submitGenerate = () => {
    if (!canSubmit || !assessPreview) return;
    onSubmit({
      memberParticleIds,
      description: description.trim(),
      directionPreview: assessPreview.promptPreview.trim(),
      promptPreview: assessPreview.promptPreview.trim(),
      model: selectedModel,
    });
  };

  return (
    <div className={TTQ_BOTTOM_PANEL_SHELL_CLASS} style={TTQ_BOTTOM_PANEL_SHELL_STYLE}>
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-ink">一解多题 · 综合同解</span>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {memberParticleIds.length} 题
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            setDescription('');
            setAssessPreview(null);
            onClose();
          }}
          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-muted hover:bg-black/[0.05]"
        >
          <X size={14} />
          关闭
        </button>
      </div>

      <div className="overflow-x-auto overscroll-x-contain px-3 py-2 [-webkit-overflow-scrolling:touch]">
        <div className="flex w-full max-w-full flex-nowrap items-stretch justify-start gap-1.5">
          <div
            className="flex shrink-0 cursor-not-allowed flex-col items-start px-1 py-1 text-left text-muted"
            style={{ width: TTQ_BOTTOM_PANEL_L1_WIDTH_PX }}
            aria-disabled
          >
            <div className="flex w-full items-start justify-between gap-2">
              <TtqAngleRelationIcon angleId="pattern_unify" size="sm" muted />
              <span className="shrink-0 text-[10px] font-medium text-muted">多对一</span>
            </div>
            <p className="mt-1 line-clamp-3 text-xs text-muted">{L1_DESCRIPTION}</p>
            <span className="mt-1 text-[10px] text-muted">先评估 · 再生成综合颗粒</span>
          </div>
        </div>
      </div>

      <div className="max-h-24 space-y-1 overflow-y-auto border-t border-border px-4 py-2">
        {memberParticleIds.map((id, index) => (
          <p key={id} className="truncate text-[11px] text-ink" title={labelOf(id)}>
            <span className="mr-1 font-medium text-ink/70">{index + 1}.</span>
            {labelOf(id)}
          </p>
        ))}
      </div>

      <div className="border-t border-border px-4 py-3">
        <label className="mb-1.5 block text-[11px] font-medium text-muted">
          补充说明（可选）
        </label>
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={2}
          placeholder="希望如何收敛解法，或补充方法核约束…"
          className="w-full resize-none rounded-lg border border-border bg-canvas/60 px-3 py-2 text-sm text-ink outline-none focus:border-primary/40"
          disabled={busy}
        />

        <div className="mt-3">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className="text-[11px] font-medium text-muted">提示词预览</p>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setAssessError(null);
                setAssessing(true);
                void onAssess({
                  memberParticleIds,
                  description: description.trim(),
                  model: selectedModel,
                }).then((result) => {
                  setAssessPreview(result);
                }).catch((fail) => {
                  setAssessPreview(null);
                  setAssessError(fail instanceof Error ? fail.message : '评估失败');
                }).finally(() => {
                  setAssessing(false);
                });
              }}
              className={
                'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium '
                + (busy
                  ? 'cursor-not-allowed border-border text-muted'
                  : 'border-primary/35 bg-primary/10 text-primary hover:bg-primary/15')
              }
            >
              {assessing ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
              {assessing ? '评估中…' : '确认提示词预览'}
            </button>
          </div>
          {assessPreview ? (
            <div className="max-h-36 space-y-2 overflow-auto rounded-lg border border-primary/20 bg-primary/[0.04] px-3 py-2 text-[11px] leading-relaxed text-ink/80">
              <p className={
                'font-medium '
                + (assessPreview.canUnify === false ? 'text-amber-800' : 'text-primary')
              }
              >
                {assessPreview.canUnify === false ? '判定：暂不宜综合同解' : '判定：可综合同解'}
              </p>
              <pre className="whitespace-pre-wrap font-sans">{assessPreview.promptPreview}</pre>
              {previewModel ? (
                <p className="flex items-center gap-1.5 text-[10px] text-muted">
                  评估模型：
                  {previewModel.iconUrl ? (
                    <img src={previewModel.iconUrl} alt="" className="h-3.5 w-3.5 rounded-sm" />
                  ) : null}
                  <span className="font-medium text-ink/70">{previewModel.label}</span>
                </p>
              ) : null}
              <p className="text-[10px] text-muted">已纳入后续生成 · 修改说明后需重新评估</p>
            </div>
          ) : (
            <p className="text-[11px] font-medium text-amber-800">
              须先点「确认提示词预览」调用评估后，才能发送给大模型
            </p>
          )}
        </div>

        {assessError || error ? (
          <p className="mt-1.5 text-xs text-danger">{assessError || error}</p>
        ) : null}
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted">
            <span className="font-medium text-ink/70">图例</span>
            <span className="inline-flex items-center gap-1"><Square size={12} />题</span>
            <span className="inline-flex items-center gap-1"><Check size={12} />解</span>
            <span className="inline-flex items-center gap-1"><MoveRight size={12} />综合</span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <TtqWorkbenchModelMenu
              value={modelId}
              disabled={busy}
              onChange={(option) => setModelId(option.id)}
            />
            <button
              type="button"
              disabled={!canSubmit}
              title={
                !hasPreview
                  ? '请先确认提示词预览'
                  : forceUnify
                    ? '评估暂不宜同解：发送前将二次确认'
                    : undefined
              }
              onClick={() => {
                if (!canSubmit || !assessPreview) return;
                if (forceUnify) {
                  const ok = window.confirm(
                    '评估判定暂不宜综合同解。\n\n若仍强行生成，综合知识颗粒往往与成员题脱节，也会拖累后续品题浓缩结果质量。\n\n确定仍要强行综合同解并生成？',
                  );
                  if (!ok) return;
                }
                submitGenerate();
              }}
              className={
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium '
                + (submitting || canSubmit
                  ? (forceUnify
                    ? 'bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-90'
                    : 'bg-primary text-white hover:bg-primary/90 disabled:opacity-90')
                  : 'cursor-not-allowed bg-black/[0.04] text-muted')
              }
            >
              {submitting ? <Loader2 size={14} className="animate-spin text-white" /> : <Send size={14} />}
              {submitting ? '生成中…' : (forceUnify ? '强行发送' : '发送给大模型')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
